export interface IClientContext {
  operatingSystem: string;
  browser: string;
  locale: string;
  timezone: string;
  pageLoadMs?: number;
}

export function resolveOperatingSystem(navigator: Navigator | undefined): string {
  try {
    const platform = (navigator as unknown as { userAgentData?: { platform?: string } })?.userAgentData?.platform ?? '';
    const haystack = `${platform} ${navigator?.userAgent ?? ''}`;
    // Order matters: iOS/Android user agents also contain "Mac"/"Linux".
    if (/Windows/i.test(haystack)) {
      return 'Windows';
    }
    if (/iPhone|iPad|iPod|iOS/i.test(haystack)) {
      return 'iOS';
    }
    if (/Mac/i.test(haystack)) {
      return 'macOS';
    }
    if (/Android/i.test(haystack)) {
      return 'Android';
    }
    if (/CrOS/i.test(haystack)) {
      return 'Chrome OS';
    }
    if (/Linux/i.test(haystack)) {
      return 'Linux';
    }
    return '';
  } catch {
    return '';
  }
}

export function resolveBrowser(navigator: Navigator | undefined): string {
  try {
    const userAgent = navigator?.userAgent ?? '';
    const major = (pattern: RegExp): string | null => userAgent.match(pattern)?.[1] ?? null;

    let name: string | null = null;
    let version: string | null = null;

    // Order matters: Edge/Opera user agents also contain "Chrome"; Chrome contains "Safari".
    if (/Edg\//.test(userAgent)) {
      name = 'Edge';
      version = major(/Edg\/(\d+)/);
    } else if (/OPR\/|Opera/.test(userAgent)) {
      name = 'Opera';
      version = major(/(?:OPR|Opera)\/(\d+)/);
    } else if (/Firefox\//.test(userAgent)) {
      name = 'Firefox';
      version = major(/Firefox\/(\d+)/);
    } else if (/Chrome\//.test(userAgent)) {
      name = 'Chrome';
      version = major(/Chrome\/(\d+)/);
    } else if (/Safari\//.test(userAgent) && /Version\//.test(userAgent)) {
      name = 'Safari';
      version = major(/Version\/(\d+)/);
    }

    if (!name) {
      return '';
    }

    return version ? `${name} ${version}` : name;
  } catch {
    return '';
  }
}

export function resolveLocale(navigator: Navigator | undefined): string {
  try {
    return navigator?.language ?? '';
  } catch {
    return '';
  }
}

/** IANA timezone (e.g. "Europe/Zurich"); the backend derives country from this. */
export function resolveTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
  } catch {
    return '';
  }
}

/**
 * Page load time in whole milliseconds from the Navigation Timing API, or undefined when not
 * measurable. Rounded to an integer so the backend never serialises a culture-specific decimal
 * separator (which would break numeric parsing in dashboards).
 */
export function resolvePageLoadMs(performance: Performance | undefined): number | undefined {
  try {
    const [navigation] = (performance?.getEntriesByType('navigation') ?? []) as PerformanceNavigationTiming[];
    return navigation && navigation.duration > 0 ? Math.round(navigation.duration) : undefined;
  } catch {
    return undefined;
  }
}

export function getClientContext(window: Window): IClientContext {
  const navigator = window?.navigator;
  return {
    operatingSystem: resolveOperatingSystem(navigator),
    browser: resolveBrowser(navigator),
    locale: resolveLocale(navigator),
    timezone: resolveTimezone(),
    pageLoadMs: resolvePageLoadMs(window?.performance),
  };
}
